import type { DraftPreviewMode } from "../timeline/draftPreviewMode";
import type { Element } from "../types";

type ClipLike = {
  elementId?: string | null;
  start: number;
  end: number;
};

type FrameDependencyArgs = {
  elements: Element[];
  clips: ClipLike[];
  playhead: number;
  activeTransitionCount: number;
};

type PixelRatioArgs = FrameDependencyArgs & {
  isPlaying: boolean;
  devicePixelRatio: number;
  draftPreviewMode: DraftPreviewMode;
};

type FilterPreviewArgs = FrameDependencyArgs & {
  isPlaying: boolean;
  draftPreviewMode: DraftPreviewMode;
};

export type VideoFilterPreviewQuality = "full" | "balanced" | "draft";

const CLIP_EPSILON_SEC = 1 / 240;

function isClipActive(clip: ClipLike, playhead: number): boolean {
  return playhead + CLIP_EPSILON_SEC >= clip.start && playhead <= clip.end + CLIP_EPSILON_SEC;
}

function hasFrameDependentAnimation(element: Element): boolean {
  const typed = element as Element & {
    keyframeTracks?: unknown[];
    motionEffects?: unknown[];
    motionPath?: { points?: unknown[] } | null;
    followTarget?: { targetElementId?: string } | null;
  };
  return (
    (Array.isArray(typed.motionEffects) && typed.motionEffects.length > 0) ||
    (Array.isArray(typed.keyframeTracks) && typed.keyframeTracks.length > 0) ||
    ((typed.motionPath?.points?.length ?? 0) >= 2) ||
    typeof typed.followTarget?.targetElementId === "string"
  );
}

function collectActiveClipIds(clips: ClipLike[], playhead: number): Set<string> {
  const out = new Set<string>();
  for (const clip of clips) {
    if (!clip.elementId) continue;
    if (isClipActive(clip, playhead)) out.add(clip.elementId);
  }
  return out;
}

function hasVisualPreviewFilters(element: Element): boolean {
  const typed = element as Element & {
    filters?: Array<{ type?: unknown; value?: unknown }>;
    photoAdjustments?: Record<string, unknown> | null;
    lutSettings?: { enabled?: unknown; lut?: unknown } | null;
  };
  if (Array.isArray(typed.filters) && typed.filters.some((filter) => Number(filter?.value) > 0)) return true;
  if (typed.lutSettings?.enabled === true && typed.lutSettings?.lut) return true;
  if (!typed.photoAdjustments || typeof typed.photoAdjustments !== "object") return false;
  return hasMeaningfulAdjustmentValue(typed.photoAdjustments);
}

function hasMeaningfulAdjustmentValue(value: unknown): boolean {
  if (typeof value === "number") return Math.abs(value) > 0.001;
  if (Array.isArray(value)) return value.some((entry) => hasMeaningfulAdjustmentValue(entry));
  if (!value || typeof value !== "object") return false;
  return Object.values(value).some((entry) => hasMeaningfulAdjustmentValue(entry));
}

export function hasActiveCanvasFrameDependency(args: FrameDependencyArgs): boolean {
  const { elements, clips, playhead, activeTransitionCount } = args;
  if (activeTransitionCount > 0) return true;

  const activeClipIds = collectActiveClipIds(clips, playhead);
  const clipBoundIds = new Set(clips.map((clip) => clip.elementId).filter((id): id is string => typeof id === "string"));

  for (const element of elements) {
    if (!hasFrameDependentAnimation(element)) continue;
    if (!clipBoundIds.has(element.id)) return true;
    if (activeClipIds.has(element.id)) return true;
  }

  return false;
}

export function resolveCanvasPlaybackPixelRatio(args: PixelRatioArgs): number {
  const {
    elements,
    clips,
    playhead,
    activeTransitionCount,
    isPlaying,
    devicePixelRatio,
    draftPreviewMode,
  } = args;

  const safeDpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  if (!isPlaying || draftPreviewMode === "off") return safeDpr;

  const activeClipIds = collectActiveClipIds(clips, playhead);
  const totalVideoElements = elements.filter((element) => element.kind === "video").length;
  let activeVideoElements = 0;
  let activeOverlayVideos = 0;
  let activeAnimatedElements = 0;

  for (const element of elements) {
    const isFrameDependent = hasFrameDependentAnimation(element);
    if (isFrameDependent && activeClipIds.has(element.id)) {
      activeAnimatedElements += 1;
    }
    if (element.kind !== "video" || !activeClipIds.has(element.id)) continue;
    activeVideoElements += 1;
    if ((element as Element & { fxOverlayMeta?: unknown }).fxOverlayMeta) {
      activeOverlayVideos += 1;
    }
  }

  let maxPixelRatio = safeDpr;
  if (draftPreviewMode === "on") {
    maxPixelRatio =
      activeVideoElements >= 8 || activeOverlayVideos >= 2 || activeAnimatedElements >= 6 || activeTransitionCount > 0
        ? 1
        : 1.25;
  } else {
    if (
      activeVideoElements >= 24 ||
      activeOverlayVideos >= 6 ||
      activeAnimatedElements >= 14 ||
      (totalVideoElements >= 100 && activeVideoElements >= 6)
    ) {
      maxPixelRatio = 1;
    } else if (
      activeVideoElements >= 10 ||
      activeOverlayVideos >= 2 ||
      activeAnimatedElements >= 5 ||
      activeTransitionCount > 0 ||
      (totalVideoElements >= 60 && activeVideoElements >= 3)
    ) {
      maxPixelRatio = 1.25;
    }
  }

  return Math.max(0.75, Math.min(safeDpr, maxPixelRatio));
}

export function resolveVideoFilterPreviewQuality(args: FilterPreviewArgs): VideoFilterPreviewQuality {
  const { elements, clips, playhead, activeTransitionCount, isPlaying, draftPreviewMode } = args;
  if (!isPlaying) return "full";

  const activeClipIds = collectActiveClipIds(clips, playhead);
  let activeVideoElements = 0;
  let activeOverlayVideos = 0;
  let activeAnimatedElements = 0;
  let activeFilteredVideos = 0;

  for (const element of elements) {
    const isActive = activeClipIds.has(element.id);
    if (isActive && hasFrameDependentAnimation(element)) {
      activeAnimatedElements += 1;
    }
    if (element.kind !== "video" || !isActive) continue;
    activeVideoElements += 1;
    if ((element as Element & { fxOverlayMeta?: unknown }).fxOverlayMeta) {
      activeOverlayVideos += 1;
    }
    if (hasVisualPreviewFilters(element)) {
      activeFilteredVideos += 1;
    }
  }

  if (draftPreviewMode === "on") {
    return activeFilteredVideos > 0 ? "draft" : "balanced";
  }

  if (
    activeFilteredVideos >= 3 ||
    activeVideoElements >= 8 ||
    activeOverlayVideos >= 2 ||
    activeAnimatedElements >= 6 ||
    activeTransitionCount > 0
  ) {
    return "draft";
  }

  if (
    activeFilteredVideos >= 1 ||
    activeVideoElements >= 4 ||
    activeAnimatedElements >= 3
  ) {
    return "balanced";
  }

  return "full";
}
